/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import TimelineItem from "examples/Timeline/TimelineItem";
import React, { useState, useEffect } from "react";

import axios from "axios";
import API_ENDPOINTS from "config/api";

function MovimientosEmpresas({ companyId }) {
  const [movimientos, setMovimientos] = useState([]);
  const [percentageChange, setPercentageChange] = useState(null); // null para detectar carga
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMovimientos = async () => {
      setLoading(true);
      try {
        const [movResponse, almacenesResponse] = await Promise.all([
          axios.get(API_ENDPOINTS.MOVIMIENTOS),
          axios.get(API_ENDPOINTS.ALMACENES),
        ]);

        let fetchedMovimientosRaw = movResponse.data || [];
        const almacenes = almacenesResponse.data || [];

        // Filtrar por empresa si hay un companyId
        if (companyId) {
          const almacenesIds = almacenes
            .filter((a) => String(a.empresa_id) === String(companyId))
            .map((a) => a.id);
          fetchedMovimientosRaw = fetchedMovimientosRaw.filter((mov) =>
            almacenesIds.includes(mov.almacen_id)
          );
        }

        const fetchedMovimientos = fetchedMovimientosRaw.map((movimiento) => ({
          color:
            movimiento.tipo_movimiento === "entrada"
              ? "success"
              : movimiento.tipo_movimiento === "salida"
                ? "error"
                : "info",
          icon:
            movimiento.tipo_movimiento === "entrada"
              ? "arrow_upward"
              : movimiento.tipo_movimiento === "salida"
                ? "arrow_downward"
                : "sync_alt",
          title: movimiento.descripcion,
          dateTime: movimiento.fecha_movimiento,
        }));
        setMovimientos(fetchedMovimientos);

        // Calcular el porcentaje de cambio
        const now = new Date();
        const currentMonth = now.getMonth();
        const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const currentYear = now.getFullYear();
        const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

        const currentMonthMovements = fetchedMovimientos.filter(
          (mov) =>
            new Date(mov.dateTime).getMonth() === currentMonth &&
            new Date(mov.dateTime).getFullYear() === currentYear
        ).length;
        const lastMonthMovements = fetchedMovimientos.filter(
          (mov) =>
            new Date(mov.dateTime).getMonth() === lastMonth &&
            new Date(mov.dateTime).getFullYear() === lastMonthYear
        ).length;

        const percentage =
          lastMonthMovements === 0
            ? currentMonthMovements * 100
            : ((currentMonthMovements - lastMonthMovements) / lastMonthMovements) * 100;

        setPercentageChange(percentage.toFixed(2));
      } catch (error) {
        console.error("Error fetching movimientos:", error);
        setPercentageChange(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMovimientos();
    const intervalId = setInterval(fetchMovimientos, 30000); // 30 segundos

    return () => clearInterval(intervalId); // limpiar el intervalo al desmontar el componente
  }, [companyId]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox pt={3} px={3}>
        <MDTypography variant="h6" fontWeight="medium">
          Movimientos Recientes entre Empresas
        </MDTypography>
        <MDBox mt={0} mb={2}>
          <MDTypography variant="button" color="text" fontWeight="regular">
            <MDTypography display="inline" variant="body2" verticalAlign="middle">
              <Icon sx={{ color: ({ palette: { success } }) => success.main }}>arrow_upward</Icon>
            </MDTypography>
            &nbsp;
            <MDTypography variant="button" color="text" fontWeight="medium">
              {loading || percentageChange === null ? "Cargando..." : `${percentageChange}%`}
            </MDTypography>{" "}
            este mes
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox p={2}>
        {loading ? (
          <MDTypography variant="button" color="text" fontWeight="regular">
            Cargando movimientos...
          </MDTypography>
        ) : (
          movimientos.map((movimiento, index) => (
            <TimelineItem
              key={index}
              color={movimiento.color}
              icon={movimiento.icon}
              title={movimiento.title}
              dateTime={movimiento.dateTime}
              lastItem={index === movimientos.length - 1}
            />
          ))
        )}
      </MDBox>
      <MDBox pt={3} px={3}>
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {loading || percentageChange === null || isNaN(Number(percentageChange))
            ? "Cargando..."
            : `${percentageChange > 0 ? "+" : ""}${percentageChange}%`}
          {percentageChange !== null && !isNaN(Number(percentageChange)) && " que la semana pasada"}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

export default MovimientosEmpresas;
